import { OperationOutput } from "./Machine";
import {
  Board,
  BoardDimension,
  defaultBoardFace,
  BoardEnd,
  boardEnds,
  BoardFaceRegion,
  JointedCount,
  MaterialInstance,
  MiterAngle,
  SignedMiterAngle,
  SQUARE_END,
  SurfaceCondition,
  worseSurface,
} from "./Materials";
import {
  PALLET_BOARD_LENGTH_IN,
  PALLET_BOARD_THICKNESS_Q,
  PALLET_BOARD_WIDTH_IN,
} from "./bench-work/pallet-geometry";
import { makeMaterial } from "./material-helpers";

/** Syntactic sugar to create a board material instance */

export function board(
  species: Board["species"] = "pallet",
  length: Board["length"] = 12,
  width: Board["width"] = 1,
  thickness: Board["thickness"] = 1,
  surface: Board["surface"] = "rough",
  jointed: { faces?: JointedCount; edges?: JointedCount } = {},
): MaterialInstance & { type: "board" } {
  return makeMaterial({
    type: "board",
    species,
    length,
    width,
    thickness,
    surface,
    // Defaults keep pre-milling content working: smooth stock is fully
    // milled, and rough boards still have one flat face and factory-cut
    // edges — true for pallet wood, which was milled once before it
    // weathered. Genuinely unmilled lumber passes explicit zeros.
    jointedFaces: jointed.faces ?? (surface === "rough" ? 1 : 2),
    jointedEdges: jointed.edges ?? 2,
  });
}

/**
 * The one board a pallet is built from — 36" × 4" × 4/4 rough pallet
 * stock. Every berth on the pallet holds this piece and prying frees
 * this piece, so the shop's free wood is a single interchangeable
 * board (see bench-work/pallet-geometry).
 */
export function palletBoard(): MaterialInstance & { type: "board" } {
  return board(
    "pallet",
    PALLET_BOARD_LENGTH_IN,
    PALLET_BOARD_WIDTH_IN,
    PALLET_BOARD_THICKNESS_Q,
  );
}

export function isBoard(material: MaterialInstance): material is Board {
  return material.type === "board";
}

/**
 * True when exactly one of the board's ends is mitered at the given
 * magnitude and the other is square — the top of a lean-to wall, cut to
 * seat a sloped roof flush. Sign is irrelevant: a lone miter on a
 * faceless board reads the same either way up (see SignedMiterAngle).
 */
export function hasOneMiteredEnd(board: Board, angle: MiterAngle): boolean {
  const { left, right } = boardEnds(board);
  const miters = [left, right].filter(
    (end) => end.kind === "mitered" && Math.abs(end.angle) === angle,
  );
  return (
    miters.length === 1 && (left.kind === "square" || right.kind === "square")
  );
}

/**
 * True when the board is a frame rail at the given stop: both ends
 * mitered at that magnitude, leaning toward each other (opposite signs —
 * see SignedMiterAngle). Equal-signed ends are a parallelogram, whose
 * corners can never close a frame.
 */
export function isMiteredFrameRail(board: Board, angle: MiterAngle): boolean {
  const { left, right } = boardEnds(board);
  return (
    left.kind === "mitered" &&
    right.kind === "mitered" &&
    Math.abs(left.angle) === angle &&
    left.angle === -right.angle
  );
}

/**
 * How a length cut lands on the board's ends. The saw's fence stop measures
 * the kept piece; `cutEnd` is the end that faces the blade — the kept
 * piece's fresh face — while its other end rides the stop untouched.
 */
export interface LengthCutSetup {
  /**
   * 0° is a square crosscut; anything else miters the fresh faces. Signed:
   * the head swings both ways, and both pieces' fresh ends record the same
   * signed angle (they share the cut line).
   */
  readonly angle: SignedMiterAngle | 0;
  /** Which end of the input board gets cut off (and freshly re-faced). */
  readonly cutEnd: "left" | "right";
}

const SQUARE_LENGTH_CUT: LengthCutSetup = { angle: 0, cutEnd: "left" };

/**
 * Takes an input board and cuts it into two boards of the specified size.
 * Length cuts track end treatments: the blade leaves a fresh face (square
 * or mitered, per `lengthCut`) on both resulting pieces, and each piece
 * keeps the input's end on its far side. Width and thickness cuts run along
 * the board, so both pieces inherit the input's ends unchanged.
 */
export function cutBoard(
  inputBoard: Board,
  outputSize: number,
  dimension: "length" | "width" | "thickness",
  waste: number = 0,
  lengthCut: LengthCutSetup = SQUARE_LENGTH_CUT,
): OperationOutput {
  const startingDimension = inputBoard[dimension];

  if (startingDimension <= outputSize) {
    throw new Error("Board is too small to cut");
  }

  const offcutSize = startingDimension - outputSize - waste;

  // Both pieces stay on the source board's face (materialized here
  // because the input's id — which a virgin board's placement is seeded
  // by — dies with the cut). Length and width cuts are the two axes of
  // the face; a thickness cut is the one that leaves it, so its offcut
  // rerolls below.
  const face = boardFaceRegion(inputBoard);

  // Each piece is a new material (makeMaterial — a fresh id): two pieces
  // sharing the input's id would be indistinguishable to everything
  // id-keyed (bench layout, hover, take), and every other operation's
  // outputs already get fresh ids.
  if (dimension !== "length") {
    const outputs = [
      makeMaterial<Board>({ ...inputBoard, [dimension]: outputSize, face }),
    ];
    if (offcutSize > 0) {
      // A rip's offcut is the far side of the face, past the blade. The
      // thickness offcut's outer face is the input's far face — wood the
      // scan never showed — so it rolls a placement of its own instead
      // (absent face: seeded by its final id).
      const { face: _parentFace, ...faceless } = inputBoard;
      outputs.push(
        makeMaterial<Board>({
          ...faceless,
          [dimension]: offcutSize,
          ...(dimension === "width"
            ? { face: { ...face, v: face.v + outputSize + waste } }
            : {}),
        }),
      );
    }
    return { inputs: [], outputs };
  }

  const { angle, cutEnd } = lengthCut;
  const freshEnd: BoardEnd =
    angle === 0 ? SQUARE_END : { kind: "mitered", angle };
  const ends = boardEnds(inputBoard);

  // The kept piece's fresh face is on the cut end; the offcut's fresh face
  // is on its side toward the blade — the opposite label. `cutEnd` names
  // the end of the input that comes OFF, so the offcut is that end's
  // portion of the face and the kept window slides to the other side
  // (past the kerf, when the cut charges one).
  const kept = makeMaterial<Board>({
    ...inputBoard,
    length: outputSize,
    ends: { ...ends, [cutEnd]: freshEnd },
    face:
      cutEnd === "left"
        ? { ...face, u: face.u + (startingDimension - outputSize) }
        : face,
  });
  const outputs = [kept];
  if (offcutSize > 0) {
    const offcutFreshEnd = cutEnd === "left" ? "right" : "left";
    outputs.push(
      makeMaterial<Board>({
        ...inputBoard,
        length: offcutSize,
        ends: { ...ends, [offcutFreshEnd]: freshEnd },
        face:
          cutEnd === "left"
            ? face
            : { ...face, u: face.u + outputSize + waste },
      }),
    );
  }

  return { inputs: [], outputs };
}

/**
 * A board's face region. Boards minted through makeMaterial always carry
 * one; the default covers boards from before the face bookkeeping
 * (fixtures, old saves).
 */
export function boardFaceRegion(board: Board): BoardFaceRegion {
  return board.face ?? defaultBoardFace(board.id, board.length, board.width);
}

/** How a resaw lands on the two pieces it makes. */
export interface ResawSetup {
  /**
   * Kerf, in thickness detents. A band saw's blade is thin enough to
   * vanish at quarter-inch granularity (0); a table saw's takes a quarter
   * off the total.
   */
  readonly waste?: number;
  /** What the blade leaves on the fresh faces — the cut's finish quality. */
  readonly sawnSurface: SurfaceCondition;
}

/**
 * Splits a board along its thickness: one piece at the fence setting, one
 * offcut, instead of planing the difference into shavings.
 *
 * The fence-side piece keeps the face that rode the fence — flat only if
 * the input had a flat face to offer — and the offcut's outer face is the
 * input's far one, flat only if the board was already planed to two.
 * Neither piece can be better than one-face-jointed afterwards, because
 * the blade leaves a fresh sawn face on each. Edges and ends are
 * untouched, so both inherit them.
 */
export function resawBoard(
  inputBoard: Board,
  targetThickness: BoardDimension,
  { waste = 0, sawnSurface }: ResawSetup,
): OperationOutput {
  const { outputs } = cutBoard(inputBoard, targetThickness, "thickness", waste);
  // One surface field for the whole board: the fresh sawn face is the
  // worst of it now (see worseSurface).
  const surface = worseSurface(inputBoard.surface, sawnSurface);
  const [fenceSide, offcut] = outputs as ReadonlyArray<Board>;

  return {
    inputs: [],
    outputs: [
      makeMaterial<Board>({
        ...fenceSide,
        jointedFaces: Math.min(inputBoard.jointedFaces, 1) as JointedCount,
        surface,
      }),
      ...(offcut
        ? [
            makeMaterial<Board>({
              ...offcut,
              // The offcut's outer face is the input's far face — flat
              // only if the board was already planed to two.
              jointedFaces: inputBoard.jointedFaces === 2 ? 1 : 0,
              surface,
            }),
          ]
        : []),
    ],
  };
}
